import {useState, useRef, useEffect, useCallback} from "react";
import {api} from "../../lib/api";
import messenger from "../../lib/messenger";
import FancySelect from "../FancySelect";
import {XForm, rules, useToggle, smoothScrollToNode, useNeoBag, SubmitButton} from "@cdx/common";
import {hasPermissionToCreateCardPreset} from "../../lib/permissions";
import {Box, Col, DSButton, DSIconButton, DSIconChevronDown, Row} from "@cdx/ds";
import {WrappedDSInput} from "@cdx/common/forms/WrappedDSInput";

const FormSubmitButton = (props) => <SubmitButton as={DSButton} formBag={useNeoBag()} {...props} />;

const extractPresetData = ({content, priority, effort, masterTags}) => ({
  content,
  priority,
  effort,
  masterTags,
});

const presetRules = {
  name: [rules.isRequired],
};

const PresetSaverForm = ({account, card}) => {
  const [open, setOpen] = useState(false);
  const handleSubmit = ({name}) =>
    api.mutate.cardPresets
      .create({data: extractPresetData(card), accountId: account.id, name})
      .then(() => {
        setOpen(false);
        messenger.send(
          <span>
            Preset <b>{name}</b> saved
          </span>
        );
      });
  return (
    <Col sp="12px" py="16px">
      <Box ml="auto">
        <DSIconButton
          variant="tertiary"
          size="sm"
          negatePadding
          icon={<DSIconChevronDown />}
          iconPosition="right"
          label="Save as Preset"
          onClick={() => setOpen((v) => !v)}
          active={open}
        />
      </Box>
      {open && (
        <Row styleChild align="center" sp="8px">
          <XForm initialValues={{name: ""}} rules={presetRules} onSubmit={handleSubmit}>
            <XForm.Field as={WrappedDSInput} autoFocus name="name" label="Preset name" size="sm" />
            <FormSubmitButton>Save</FormSubmitButton>
          </XForm>
        </Row>
      )}
    </Col>
  );
};

export const PresetSaver = ({root, card}) => {
  const {account} = root;
  return hasPermissionToCreateCardPreset(root) ? (
    <PresetSaverForm account={account} card={card} />
  ) : null;
};

export const PresetPicker = ({card, root, presetId, onSetPresetId}) => {
  const [isOpen, {toggle, off}] = useToggle();

  const prevIdRef = useRef(card.id);
  useEffect(() => {
    if (prevIdRef.current !== card.id) off();
  }, [off, card.id]);
  useEffect(() => {
    prevIdRef.current = card.id;
  }, [card.id]);
  const containerRef = useRef(null);

  const presetIdRef = useRef(presetId);
  useEffect(() => {
    presetIdRef.current = presetId;
  }, [presetId]);
  useEffect(() => {
    prevIdRef.current = card.id;
  }, [card.id]);

  const selectRefFn = useCallback((n) => {
    if (n && !presetIdRef.current) {
      n.activate();
      setTimeout(() => {
        if (containerRef.current) smoothScrollToNode(containerRef.current);
      }, 20);
    }
  }, []);

  if (!root.account.$meta.exists("cardPresets")) return null;

  return (
    <Col sp="12px" py="16px">
      <Box ml="auto">
        <DSIconButton
          variant="tertiary"
          size="sm"
          negatePadding
          icon={<DSIconChevronDown />}
          iconPosition="right"
          label="Presets"
          onClick={toggle}
          active={isOpen}
        />
      </Box>
      {isOpen && (
        <Col style={{paddingBottom: 200}} ref={containerRef} sp="12px">
          <FancySelect
            getOptions={() => root.account.cardPresets}
            extractKey={(p) => p.id}
            instanceToValue={(p) => p.id}
            getSearchText={(p) => p.name}
            onChange={onSetPresetId}
            value={presetId}
            ref={selectRefFn}
          >
            {(p) => p.name}
          </FancySelect>
          {presetId && (
            <Box ml="auto">
              <DSButton variant="tertiary" size="sm" onClick={() => onSetPresetId(null)}>
                Reset
              </DSButton>
            </Box>
          )}
        </Col>
      )}
    </Col>
  );
};
